import { formatPacketWithByteIndexes } from './PacketFormatter';
import {
  DeviceType,
  buildCommandByte,
  matchesProtocolResponse,
  parseCommandByte,
} from './ProtocolHeader';
import { parseDeviceVidPid } from './DeviceCatalog';
import { readDeviceConfigMac } from './DeviceIdentity';
import {
  LIGHTING_COMMAND,
  LIGHTING_EFFECT_SWITCH_TYPE,
  LIGHTING_EFFECT_WRITE_TYPE,
  LIGHTING_READ_TYPE,
  LIGHTING_WRITE_TYPE,
  encodeEffectSwitchPayload,
  encodeEffectWritePayload,
  encodeLightingPayload,
  splitLightingPackets,
} from './LightingProtocol';
import { recordProtocol } from './ProtocolLog';
import {
  V4_TYPE3_CUSTOM_RATE_MIN_LENGTH,
  V4_TYPE3_MIN_LENGTH,
  V4_TYPE3_OFFSETS,
  assertType3Packet,
  writeUint16LE,
} from './PerformanceType3';
import {
  clampCustomReportRateHz,
  encodeReportRateByte,
  pollingRateHzToLevel,
} from './PollingRates';
import {
  applyButtonPerformanceToPacket,
  canLinkButtons,
} from './ButtonPerformance';
import { applyWheelSettingsToPacket } from './WheelProtocol';
import {
  RT_MONITOR_START,
  RT_MONITOR_STOP,
  encodeRtGearControl,
  encodeRtMonitorControl,
} from './RtMonitor';
import {
  RT_CALIBRATE_SAVE,
  encodeRtCalibrateControl,
} from './RtCalibrate';
import {
  RT_CALIBRATE_CONFIG_ACK_TIMEOUT_MS,
  RT_CALIBRATE_CONFIG_RESULT_TIMEOUT_MS,
  encodeRtCalibrateConfigControl,
  encodeRtCalibrateConfigSave,
  encodeRtCalibrateConfigStart as buildRtCalibrateConfigStartPacket,
  isRtCalibrateConfigAck,
  isRtCalibrateConfigResult,
  parseRtCalibrateConfigResult,
} from './RtCalibrateConfig';

/**
 * 宏读取请求的 byte2：bit7 = 本次请求到此结束，低 7 位 = 请求头长度。
 * 请求只有一包，必须带 bit7，否则设备会继续等后续请求包而不回应。
 */
const MACRO_REQUEST_LENGTH_BYTE = 0x83;

/**
 * 同一物理 HIDDevice 上所有 HIDSender 实例共享的收发队列。
 *
 * 选择页探测（probeReceiverDevice / probeDeviceBattery）会为已连接的接收器
 * 另建 HIDSender；队列若是实例级的，探测命令就会插进主 sender 的
 * 「发请求→等响应」事务中间，而设备一次只应答最后一条命令，先发那条的
 * 响应会被抢走（表现为多包宏读取时不时中断）。队列必须按设备共享。
 */
const deviceQueuesByDevice = new WeakMap();

function getDeviceQueues(device) {
  let queues = deviceQueuesByDevice.get(device);
  if (!queues) {
    queues = { write: Promise.resolve(), request: Promise.resolve() };
    deviceQueuesByDevice.set(device, queues);
  }
  return queues;
}

/**
 * HID 设备发送者类
 * 负责封装发送到HID设备的命令和数据
 */
class HIDSender {
  constructor(device) {
    this.device = device;
    /** device 为空时的兜底队列；正常路径使用按设备共享的队列 */
    this._fallbackQueues = {
      write: Promise.resolve(),
      request: Promise.resolve(),
    };
    /** 默认协议包长度（不含 reportId 字节） */
    this.defaultReportSize = 64;
    /** 成功发送后锁定的 output 端点，后续直接复用 */
    this.lockedEndpoint = null; // { reportId, size }
    /** 当前设备的驱动协议版本；完成版本探测前为 null */
    this.protocolVersion = null;
    /** 当前连接方式；用于访问真实目标设备时的 byte0 路由。 */
    this.connectionType = 'wired';
    /** 目标设备不在线时阻止普通协议读写；接收器状态探测可显式绕过。 */
    this.deviceAccessAllowed = true;
    /** 需要回应的请求包：单次等待响应的超时时间(ms)，超时即重发 */
    this.requestTimeout = 100;
    /** 需要回应的请求包：首发之外的最大重发次数 */
    this.requestMaxRetries = 5;
  }

  /**
   * 更新设备引用
   * @param {HIDDevice} device - HID设备对象
   */
  setDevice(device) {
    this.device = device;
    this.lockedEndpoint = null;
    this.protocolVersion = null;
    this.connectionType = 'wired';
    this.deviceAccessAllowed = true;
  }

  setConnectionType(connectionType) {
    this.connectionType = ['wireless', 'mcu2'].includes(connectionType)
      ? connectionType
      : 'wired';
  }

  setDeviceAccessAllowed(allowed) {
    this.deviceAccessAllowed = Boolean(allowed);
  }

  getTargetDeviceType() {
    if (this.connectionType === 'wireless') return DeviceType.WIRELESS;
    if (this.connectionType === 'mcu2') return DeviceType.MCU2;
    return DeviceType.USB;
  }

  /**
   * 统一设置 byte0 的 deviceType，同时保留 command 和 bit7。
   * - 无线连接：所有发往目标鼠标的 command 使用 WIRELESS。
   * - MCU2：当前仅 command 0/1 使用 MCU2。
   * - 接收器自身命令可通过 options.deviceType 显式指定 USB。
   */
  normalizeCommandRouting(data, options = {}) {
    const payload =
      data instanceof Uint8Array
        ? data.slice()
        : new Uint8Array(data || []);
    if (payload.length === 0) return payload;
    const header = parseCommandByte(payload[0]);
    const explicitDeviceType = options.deviceType;
    const deviceType =
      explicitDeviceType != null
        ? explicitDeviceType
        : this.connectionType === 'wireless'
          ? DeviceType.WIRELESS
          : this.connectionType === 'mcu2' &&
              (header.command === 0 || header.command === 1)
            ? DeviceType.MCU2
            : DeviceType.USB;
    payload[0] = buildCommandByte(
      header.command,
      deviceType,
      header.bit7
    );
    return payload;
  }

  /**
   * 确保设备已 open
   */
  async ensureOpen() {
    if (!this.device) {
      throw new Error('设备未连接');
    }
    if (!this.device.opened) {
      await this.device.open();
    }
  }

  /**
   * 计算 report items 占用字节数
   * @param {Array} items
   * @returns {number}
   */
  static itemsToBytes(items = []) {
    const bits = items.reduce((sum, item) => {
      const count = item.reportCount || 0;
      const size = item.reportSize || 0;
      return sum + count * size;
    }, 0);
    return Math.ceil(bits / 8);
  }

  /**
   * 是否厂商自定义 collection（0xFF00-0xFFFF）
   * @param {number} usagePage
   */
  static isVendorPage(usagePage) {
    const page = Number(usagePage) || 0;
    return page >= 0xff00 && page <= 0xffff;
  }

  /**
   * 给 output report 打分：越大越像本驱动协议通道
   * @param {{reportId:number,size:number,usagePage?:number,usage?:number}} report
   */
  scoreOutputReport(report) {
    let score = 0;
    if (report.size === this.defaultReportSize) score += 100;
    else if (report.size >= 32) score += 40;
    else if (report.size >= 8) score += 10;
    if (HIDSender.isVendorPage(report.usagePage)) score += 50;
    // 常见 RAW HID
    if (report.usagePage === 0xff60 || report.usagePage === 0xff0a) score += 20;
    // reportId=0 不一定最好；描述符里明确存在的 id 更可信
    if (report.reportId === 0) score += 5;
    return score;
  }

  /**
   * 枚举设备 output reports（含 usagePage）
   * @returns {{reportId:number, size:number, usagePage:number, usage:number}[]}
   */
  listOutputReports() {
    const device = this.device;
    const list = [];
    if (!device?.collections?.length) return list;

    for (const collection of device.collections) {
      const usagePage = collection.usagePage ?? 0;
      const usage = collection.usage ?? 0;
      for (const report of collection.outputReports || []) {
        const bytes = HIDSender.itemsToBytes(report.items || []);
        if (bytes > 0) {
          list.push({
            reportId: report.reportId ?? 0,
            size: bytes,
            usagePage,
            usage,
          });
        }
      }
    }
    return list;
  }

  /**
   * 枚举 input reports（帮助确认是否选对接口）
   */
  listInputReports() {
    const device = this.device;
    const list = [];
    if (!device?.collections?.length) return list;
    for (const collection of device.collections) {
      for (const report of collection.inputReports || []) {
        const bytes = HIDSender.itemsToBytes(report.items || []);
        if (bytes > 0) {
          list.push({
            reportId: report.reportId ?? 0,
            size: bytes,
            usagePage: collection.usagePage ?? 0,
            usage: collection.usage ?? 0,
          });
        }
      }
    }
    return list;
  }

  /**
   * 解析首选 output 端点
   * @returns {{reportId:number, size:number}|null}
   */
  resolvePreferredEndpoint() {
    if (this.lockedEndpoint) return this.lockedEndpoint;
    const reports = this.listOutputReports();
    if (!reports.length) {
      return { reportId: 0, size: this.defaultReportSize };
    }
    const sorted = [...reports].sort(
      (a, b) => this.scoreOutputReport(b) - this.scoreOutputReport(a)
    );
    const best = sorted[0];
    return { reportId: best.reportId, size: best.size };
  }

  /**
   * 生成尝试顺序：锁定端点 → 首选 → 全部 output 组合 → 常见兜底
   * @param {number} [forcedReportId]
   * @returns {{reportId:number, size:number}[]}
   */
  buildEndpointCandidates(forcedReportId) {
    const out = [];
    const push = (reportId, size) => {
      const id = Number(reportId) || 0;
      const sz = Number(size) || 0;
      if (sz <= 0) return;
      if (out.some((x) => x.reportId === id && x.size === sz)) return;
      out.push({ reportId: id, size: sz });
    };

    if (this.lockedEndpoint) {
      push(this.lockedEndpoint.reportId, this.lockedEndpoint.size);
    }

    const preferred = this.resolvePreferredEndpoint();
    if (preferred) push(preferred.reportId, preferred.size);

    if (forcedReportId != null) {
      push(forcedReportId, this.defaultReportSize);
      for (const r of this.listOutputReports()) {
        if (r.reportId === forcedReportId) push(r.reportId, r.size);
      }
    }

    // 高分 output 全试一遍
    const ranked = [...this.listOutputReports()].sort(
      (a, b) => this.scoreOutputReport(b) - this.scoreOutputReport(a)
    );
    for (const r of ranked) push(r.reportId, r.size);

    // 无描述符时的兜底
    push(0, this.defaultReportSize);
    push(0, 63);
    push(1, this.defaultReportSize);
    push(1, 63);

    return out;
  }

  /**
   * 从 HID 集合推断 output report 载荷长度
   * @param {number} reportId
   * @returns {number}
   */
  getOutputReportSize(reportId = 0) {
    const ep = this.resolvePreferredEndpoint();
    if (ep && (reportId == null || ep.reportId === reportId)) return ep.size;

    const reports = this.listOutputReports().filter((r) => r.reportId === reportId);
    if (reports.length) {
      const hit64 = reports.find((r) => r.size === this.defaultReportSize);
      if (hit64) return hit64.size;
      return Math.max(...reports.map((r) => r.size));
    }
    return this.defaultReportSize;
  }

  /**
   * 将数据对齐到目标 report 长度：截断或右侧补 0
   * @param {Uint8Array} data
   * @param {number} size
   * @returns {Uint8Array}
   */
  normalizeReportPayload(data, size) {
    const src = data instanceof Uint8Array ? data : new Uint8Array(data || []);
    if (src.length === size) return new Uint8Array(src);
    const out = new Uint8Array(size);
    out.set(src.subarray(0, Math.min(src.length, size)));
    return out;
  }

  /**
   * 设置数据包校验和（协议：前 63 字节求和，第 64 字节 = 0xA1 - sum）
   * 若缓冲短于 64，则写在最后一字节
   * @param {Uint8Array} buf - 数据缓冲区
   */
  setPacketSequence(buf) {
    if (!buf || buf.length < 2) return;
    const checksumIndex = buf.length >= 64 ? 63 : buf.length - 1;
    let sum = 0;
    for (let i = 0; i < checksumIndex; i++) {
      sum += buf[i];
    }
    buf[checksumIndex] = (0xa1 - (sum & 0xff)) & 0xff;
  }

  /**
   * 转换为十六进制字符串
   * @param {Uint8Array} data - 数据
   * @returns {string} 十六进制字符串
   */
  toHexString(data) {
    return Array.from(data)
      .map((byte) => byte.toString(16).padStart(2, '0').toUpperCase())
      .join(' ');
  }

  /**
   * 取当前设备对应的共享队列；队列必须在入队时解析，
   * setDevice 换设备后新任务要排到新设备的队列上。
   */
  _queueSlot() {
    return this.device ? getDeviceQueues(this.device) : this._fallbackQueues;
  }

  /**
   * 将写操作串入队列，避免浏览器/设备并发写失败
   * @template T
   * @param {() => Promise<T>} task
   * @returns {Promise<T>}
   */
  enqueueWrite(task) {
    const slot = this._queueSlot();
    const run = slot.write.then(task, task);
    slot.write = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }

  /**
   * 将「发请求 → 等这条请求的响应」整体串行化。
   *
   * 设备同一时刻只应答一条请求：并发发出两条时，先发的那条永远等不到自己的
   * 响应，只能等到超时重发，而调用方看到的现象是流程莫名其妙地回到了别的
   * 步骤（例如多包宏读到一半又从第 0 包重新开始）。enqueueWrite 只串行化写
   * 动作，管不到「等响应」这段，所以事务级串行必须单独排队。
   *
   * @template T
   * @param {() => Promise<T>} task
   * @returns {Promise<T>}
   */
  enqueueRequest(task) {
    const slot = this._queueSlot();
    const run = slot.request.then(task, task);
    slot.request = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }

  /**
   * 枚举 feature reports
   */
  listFeatureReports() {
    const device = this.device;
    const list = [];
    if (!device?.collections?.length) return list;
    for (const collection of device.collections) {
      for (const report of collection.featureReports || []) {
        const bytes = HIDSender.itemsToBytes(report.items || []);
        if (bytes > 0) {
          list.push({
            reportId: report.reportId ?? 0,
            size: bytes,
            usagePage: collection.usagePage ?? 0,
            usage: collection.usage ?? 0,
          });
        }
      }
    }
    return list;
  }

  /**
   * 发送输出报告（Output Report）；失败时回退 Feature Report
   * @param {number} reportId - 调用方可传 0；实际会按设备描述符自动选择
   * @param {Uint8Array} data - 数据
   * @returns {Promise<void>}
   */
  async sendOutputReport(reportId, data, options = {}) {
    return this.enqueueWrite(async () => {
      if (!this.deviceAccessAllowed && !options.allowWhenOffline) {
        throw new Error('目标设备不在线，已阻止发送协议命令');
      }
      await this.ensureOpen();
      const routedData = this.normalizeCommandRouting(data, options);

      // 已锁定端点：直接发
      if (this.lockedEndpoint) {
        const ep = this.lockedEndpoint;
        const payload = this.normalizeReportPayload(routedData, ep.size);
        this.setPacketSequence(payload);
        const sendFn =
          ep.kind === 'feature'
            ? (id, buf) => this.device.sendFeatureReport(id, buf)
            : (id, buf) => this.device.sendReport(id, buf);
        console.log(
          `发送 ${ep.kind || 'output'} Report, reportId=${ep.reportId}, len=${payload.length}`
        );
        console.log(`发送数据（HEX/Byte索引）:\n${formatPacketWithByteIndexes(payload)}`);
        try {
          await sendFn(ep.reportId, payload);
        } catch (error) {
          recordProtocol(
            'error',
            `发送失败 ${ep.kind} id=${ep.reportId}: ${error?.message || error}`,
            payload
          );
          throw error;
        }
        console.log('发送成功');
        recordProtocol('tx', `${ep.kind || 'output'} id=${ep.reportId}`, payload);
        return { success: true, ...ep };
      }

      const candidates = this.buildEndpointCandidates(reportId).map((ep) => ({
        ...ep,
        kind: 'output',
      }));

      // feature 回退候选
      const features = this.listFeatureReports()
        .sort((a, b) => this.scoreOutputReport(b) - this.scoreOutputReport(a))
        .map((r) => ({ reportId: r.reportId, size: r.size, kind: 'feature' }));
      for (const f of features) {
        if (
          !candidates.some(
            (c) => c.kind === 'feature' && c.reportId === f.reportId && c.size === f.size
          )
        ) {
          candidates.push(f);
        }
      }
      // 常见 feature 兜底
      candidates.push(
        { reportId: 0, size: this.defaultReportSize, kind: 'feature' },
        { reportId: 1, size: this.defaultReportSize, kind: 'feature' }
      );

      let lastError = null;

      for (const ep of candidates) {
        const payload = this.normalizeReportPayload(routedData, ep.size);
        this.setPacketSequence(payload);
        const sendFn =
          ep.kind === 'feature'
            ? (id, buf) => this.device.sendFeatureReport(id, buf)
            : (id, buf) => this.device.sendReport(id, buf);

        try {
          console.log(
            `发送 ${ep.kind} Report, reportId=${ep.reportId}, len=${payload.length}`
          );
          console.log(`发送数据（HEX/Byte索引）:\n${formatPacketWithByteIndexes(payload)}`);
          await sendFn(ep.reportId, payload);
          this.lockedEndpoint = {
            reportId: ep.reportId,
            size: ep.size,
            kind: ep.kind,
          };
          console.log(
            `发送成功 (locked ${ep.kind} reportId=${ep.reportId}, size=${ep.size})`
          );
          recordProtocol('tx', `${ep.kind} id=${ep.reportId} (locked)`, payload);
          return { success: true, ...this.lockedEndpoint };
        } catch (error) {
          lastError = error;
          const msg = error?.message || String(error);
          console.error(
            `发送失败 ${ep.kind} reportId=${ep.reportId} len=${ep.size}:`,
            msg
          );
          if (
            !/Failed to write the report|Failed to write report|Invalid access|NotAllowedError|NetworkError|Failed to write the feature report/i.test(
              msg
            )
          ) {
            // 非“写失败”类错误也继续试其它端点更稳妥
          }
        }
      }

      const outs = this.listOutputReports();
      const feats = this.listFeatureReports();
      const detailOut =
        outs.length > 0
          ? outs
              .map(
                (r) =>
                  `out id=${r.reportId}/size=${r.size}/page=0x${Number(
                    r.usagePage
                  ).toString(16)}`
              )
              .join(', ')
          : '无 output';
      const detailFeat =
        feats.length > 0
          ? feats
              .map(
                (r) =>
                  `feat id=${r.reportId}/size=${r.size}/page=0x${Number(
                    r.usagePage
                  ).toString(16)}`
              )
              .join(', ')
          : '无 feature';

      const msg = lastError?.message || String(lastError || 'unknown');
      console.error('所有端点均失败:', detailOut, '|', detailFeat);
      const friendly = new Error(
        /Failed to write the report|Failed to write report|feature report/i.test(msg)
          ? `写入设备失败。请断开后点「添加新设备」重选接口。当前: ${detailOut}; ${detailFeat}`
          : msg
      );
      friendly.cause = lastError;
      throw friendly;
    });
  }
  /**
   * 解析驱动协议版本查询响应。
   *
   * 新协议响应格式：
   * - byte0 = 0
   * - byte3 = 0xFF
   * - byte4-7 = 小端 UInt32 版本号
   *
   * 版本编码沿用 MCU2 的 0xMMNP 规则：
   * - MM（高字节）是主版本；
   * - N（低字节高 4 位）是次版本；
   * - P（低字节低 4 位）是修订版本。
   *
   * 例如 0x00000300 → V3.0；0x00000312 → V3.1.2。
   *
   * 旧协议返回 command=31 (0x1F)，识别为当前基线版本 V1.06。
   *
   * @param {Uint8Array|ArrayBuffer} data 设备返回的原始报告
   * @returns {{version:string,raw:number|null,legacy:boolean}|null}
   *   格式不匹配时返回 null
   */
  static parseProtocolVersionResponse(data) {
    const response =
      data instanceof Uint8Array ? data : new Uint8Array(data || []);
    if (response.length === 0) return null;

    const header = parseCommandByte(response[0]);
    if (header.command === 0x1f) {
      return {
        version: 'V1.06',
        raw: null,
        legacy: true,
      };
    }

    if (
      response.length < 8 ||
      header.command !== 0 ||
      response[3] !== 0xff
    ) {
      return null;
    }

    const raw = new DataView(
      response.buffer,
      response.byteOffset,
      response.byteLength
    ).getUint32(4, true);
    const versionWord = raw & 0xffff;
    const major = (versionWord >> 8) & 0xff;
    const minor = (versionWord >> 4) & 0x0f;
    const patch = versionWord & 0x0f;
    return {
      version:
        patch === 0
          ? `V${major}.${minor}`
          : `V${major}.${minor}.${patch}`,
      raw,
      legacy: false,
    };
  }

  /**
   * 获取驱动协议版本号。
   * 请求头 command/type 固定为 command=0/type=FF；byte0 的 deviceType
   * 根据目标设备路由（USB=00、MCU2=20、wireless=40）。
   * @param {number} timeout - 等待响应的超时时间(ms)
   * @returns {Promise<Uint8Array|null>} 设备原始响应，超时返回 null
   */
  async getProtocolVersion(timeout = 1000) {
    console.log('获取驱动协议版本号...');
    const data = new Uint8Array(64);
    data[0] = buildCommandByte(0, this.getTargetDeviceType());
    data[1] = 0x00;
    data[2] = 0x81;
    data[3] = 0xff;

    // 必须按版本响应格式过滤；无线接收器可能同时主动上报 command=1/type=3，
    // 不能把状态包误当作版本响应。
    // 整个「发请求→等响应」要占住请求队列，否则其它 sender（如选择页探测）
    // 的命令会插进等待窗口，设备只答最后一条，版本响应就丢了。
    const response = await this.enqueueRequest(async () => {
      const waiter = this.createResponseWaiter(
        (resp) => HIDSender.parseProtocolVersionResponse(resp) != null
      );
      try {
        await this.sendOutputReport(0, data);
      } catch (error) {
        waiter.cancel();
        throw error;
      }
      return waiter.wait(timeout);
    });
    if (response) {
      console.log(
        `[协议版本] 原始响应（HEX/Byte索引）:\n${formatPacketWithByteIndexes(response)}`
      );
      console.log('[协议版本] 原始响应 (bytes):', Array.from(response));

      const parsed = HIDSender.parseProtocolVersionResponse(response);
      if (parsed) {
        this.protocolVersion = parsed.version;
        console.log(
          '[协议版本] 已识别:',
          this.protocolVersion,
          parsed.raw == null
            ? '(legacy command 0x1F)'
            : `(raw=0x${parsed.raw
                .toString(16)
                .padStart(8, '0')
                .toUpperCase()})`
        );
      } else {
        this.protocolVersion = null;
        console.log(
          `[协议版本] 无法识别响应 command=0x${response[0]
            .toString(16)
            .padStart(2, '0')
            .toUpperCase()}`
        );
      }
    } else {
      this.protocolVersion = null;
      console.warn(`[协议版本] 等待响应超时 (${timeout}ms)`);
    }
    return response;
  }

  /**
   * 获取设备配置（命令0）
   * @returns {Promise<void>}
   */
  async getDeviceConfig() {
    console.log('获取设备配置...');
    const data = new Uint8Array(64);
    data[0] = buildCommandByte(0, this.getTargetDeviceType());
    data[2] = 0x80 | 1;
    data[3] = 0x0;
    return await this.sendOutputReport(0, data);
  }

  /**
   * 获取设备配置并等待 command=0/type=0 的响应。
   * 初始化链路使用事务队列按回包推进，避免依赖固定延时。
   */
  async requestDeviceConfig() {
    const data = new Uint8Array(64);
    data[0] = buildCommandByte(0, this.getTargetDeviceType());
    data[2] = 0x80 | 1;
    data[3] = 0x00;
    return this.sendRequestWithRetry(
      data,
      { command: 0, type: 0 },
      { label: '获取设备配置' }
    );
  }

  /**
   * 读取设备配置并只取 byte10-14 的 MAC，供选择页识别同型号的不同设备。
   * @param {number} timeout
   * @returns {Promise<string|null>} `AA:BB:CC:DD:EE`；未烧录 MAC 时返回 null
   */
  async requestDeviceMac(timeout = 900) {
    const data = new Uint8Array(64);
    data[0] = buildCommandByte(0, this.getTargetDeviceType());
    data[2] = 0x80 | 1;
    data[3] = 0x00;
    const response = await this.sendRequestWithRetry(
      data,
      { command: 0, type: 0 },
      { timeout, maxRetries: 0, label: '读取设备 MAC' }
    );
    return readDeviceConfigMac(response);
  }

  /**
   * 获取通告信息（命令1）
   * @returns {Promise<void>}
   */
  async getInformationData() {
    console.log('获取设备通告信息...');
    const data = new Uint8Array(64);
    data[0] = buildCommandByte(1, this.getTargetDeviceType());
    data[1] = 0x00;
    data[2] = 0x80 | 1;
    data[3] = 0x01;
    return await this.sendOutputReport(0, data);
  }

  /**
   * 发送通告信息请求并等待 command=1/type=1 的响应（含电量字段）。
   * 探测类调用必须走这里而不是裸调 getInformationData + waitForResponse：
   * 事务要占住请求队列，避免与同设备上其它 sender 的读事务互相抢响应。
   * @param {number} timeout - 等待响应的超时时间(ms)
   * @returns {Promise<Uint8Array|null>} 响应数据，超时返回 null
   */
  async requestInformationData(timeout = 900) {
    return this.enqueueRequest(async () => {
      const waiter = this.createResponseWaiter((raw) => {
        if (!raw || raw.length < 12) return false;
        return parseCommandByte(raw[0]).command === 1 && raw[3] === 1;
      });
      try {
        await this.getInformationData();
      } catch (error) {
        waiter.cancel();
        throw error;
      }
      return waiter.wait(timeout);
    });
  }

  /**
   * 查询通用 Dongle 对应设备状态。此命令始终发给 USB 接收器自身。
   * @param {number} timeout
   * @returns {Promise<{
   *   raw:Uint8Array,
   *   deviceState:number,
   *   reportRateMax:number,
   *   targetVendorId:number|null,
   *   targetProductId:number|null
   * }|null>}
   */
  async getReceiverStatus(timeout = 1000) {
    const data = new Uint8Array(64);
    data[0] = buildCommandByte(1, DeviceType.USB);
    data[1] = 0;
    data[2] = 0x81;
    data[3] = 3;

    // 同样必须占住请求队列：状态探测和主 sender 的读事务共用同一台设备，
    // 不排队会互相抢响应。
    const response = await this.enqueueRequest(async () => {
      const waiter = this.createResponseWaiter({ command: 1, type: 3 });
      try {
        await this.sendOutputReport(0, data, {
          allowWhenOffline: true,
          deviceType: DeviceType.USB,
        });
      } catch (error) {
        waiter.cancel();
        throw error;
      }
      return waiter.wait(timeout);
    });
    return response ? HIDSender.parseReceiverStatusResponse(response) : null;
  }

  static parseReceiverStatusResponse(data) {
    const response =
      data instanceof Uint8Array
        ? data
        : data instanceof ArrayBuffer
          ? new Uint8Array(data)
          : data?.buffer && typeof data.byteLength === 'number'
            ? new Uint8Array(
                data.buffer.slice(
                  data.byteOffset || 0,
                  (data.byteOffset || 0) + data.byteLength
                )
              )
            : new Uint8Array(data || []);
    if (response.length < 10) {
      return null;
    }
    const header = parseCommandByte(response[0]);
    // 主动查询响应和设备 notify 均使用 command=1/type=3。
    // 匹配时忽略 byte0 的 deviceType/bit7。
    if (header.command !== 1 || response[3] !== 3) {
      return null;
    }
    const identity = parseDeviceVidPid(response, 6);
    return {
      raw: response.slice(),
      deviceState: response[4],
      reportRateMax: response[5],
      targetVendorId: identity?.vendorId ?? null,
      targetProductId: identity?.productId ?? null,
      targetVidPidRaw: identity?.raw ?? null,
    };
  }

  /**
   * 获取鼠标按键信息（命令3）
   * @returns {Promise<void>}
   */
  async getMouseButtonData() {
    console.log('获取鼠标按键信息...');
    const data = new Uint8Array(64);
    data[0] = 0x03;  // 命令3
    data[1] = 0x00;
    data[2] = 0x80 | 1;
    data[3] = 1;     // 固定为1
    return await this.sendOutputReport(0, data);
  }

  /**
   * 获取鼠标按键信息并等待 command=3/type=1 的响应。
   */
  async requestMouseButtonData() {
    const data = new Uint8Array(64);
    data[0] = 0x03;
    data[1] = 0x00;
    data[2] = 0x80 | 1;
    data[3] = 1;
    return this.sendRequestWithRetry(
      data,
      { command: 3, type: 1 },
      { label: '获取鼠标按键信息' }
    );
  }

  /**
   * 获取性能数据（命令4）
   * @param {number} type - 数据类型（1=传感器设置, 3=系统设置）
   * @returns {Promise<void>}
   */
  async getPerformanceData(type = 3) {
    console.log(`获取设备性能数据 (类型 ${type})...`);
    const data = new Uint8Array(64);
    data[0] = 0x04;  // 命令4
    data[1] = 0x00;
    data[2] = 0x80 | 1;
    data[3] = type;
    return await this.sendOutputReport(0, data);
  }

  /**
   * 获取性能数据并等待 command=4/指定 type 的响应。
   * @param {number} type - 数据类型（1=传感器设置, 3=系统设置）
   */
  async requestPerformanceData(type = 3) {
    const data = new Uint8Array(64);
    data[0] = 0x04;
    data[1] = 0x00;
    data[2] = 0x80 | 1;
    data[3] = type;
    return this.sendRequestWithRetry(
      data,
      { command: 4, type },
      { label: `获取设备性能数据 (类型 ${type})` }
    );
  }

  /**
   * 获取颜色数据（命令5）
   * @param {number} type - 数据类型
   * @returns {Promise<void>}
   */
  async getColorData(type = 0) {
    console.log(`获取设备颜色数据 (类型 ${type})...`);
    const data = new Uint8Array(64);
    data[0] = 0x05;  // 命令5
    data[1] = 0x00;
    data[2] = 0x80 | 1;
    data[3] = type;
    return await this.sendOutputReport(0, data);
  }

  /**
   * 获取旧协议颜色数据并等待 command=5/指定 type 的响应。
   * @param {number} type - 数据类型
   */
  async requestColorData(type = 0) {
    const data = new Uint8Array(64);
    data[0] = 0x05;
    data[1] = 0x00;
    data[2] = 0x80 | 1;
    data[3] = type;
    return this.sendRequestWithRetry(
      data,
      { command: 5, type },
      { label: `获取设备颜色数据 (类型 ${type})` }
    );
  }

  /**
   * 获取宏数据（命令7）
   * @param {number} type - 数据类型
   * @returns {Promise<void>}
   */
  async getMacroData(type = 1) {
    console.log(`获取宏数据 (类型 ${type})...`);
    const data = new Uint8Array(64);
    data[0] = 0x07;  // 命令7
    data[1] = 0x00;
    data[2] = MACRO_REQUEST_LENGTH_BYTE;
    data[3] = type;
    return await this.sendMacroDataRequestWithRetry(data, 0, type);
  }

  /**
   * 获取指定索引的宏数据包（命令7）
   *
   * 除包索引外必须和 getMacroData 的请求完全一致：byte2 的 bit7 是「本次请求
   * 到此为止」标志，缺了它设备会一直等后续请求包而不回应，多包宏就永远停在
   * 第 0 包。（曾经这里写的是 0x3b，是全文件唯一没带 bit7 的请求。）
   *
   * @param {number} packetIndex - 包索引
   * @returns {Promise<void>}
   */
  async getMacroDataByIndex(packetIndex) {
    console.log(`获取宏数据包 (索引 ${packetIndex})...`);
    const data = new Uint8Array(64);
    data[0] = 0x07;  // 命令7
    data[1] = packetIndex;  // 包索引
    data[2] = MACRO_REQUEST_LENGTH_BYTE;
    data[3] = 1;
    return await this.sendMacroDataRequestWithRetry(data, packetIndex, 1);
  }

  /**
   * 发送需要设备回应的请求包：默认 100ms 内没有回应就重发同一包，
   * 最多重发 5 次；全部无响应才抛错。
   *
   * 设备偶发漏包时不重发会让整条读取链路永久中断，因此所有「发请求 →
   * 等响应」的读命令都应走这里，而不是各自裸调 waitForResponse。
   *
   * @param {Uint8Array} data - 已构建好的 64 字节请求包
   * @param {number|null|object|Function} expectedResponse - 期望响应描述
   * @param {{timeout?:number,maxRetries?:number,label?:string,reportId?:number,sendOptions?:object}} options
   * @returns {Promise<Uint8Array>} 匹配到的响应；重试耗尽则抛错
   */
  async sendRequestWithRetry(data, expectedResponse, options = {}) {
    const {
      timeout = this.requestTimeout,
      maxRetries = this.requestMaxRetries,
      label = '协议请求',
      reportId = 0,
      sendOptions,
    } = options;
    const maxAttempts = Math.max(1, (Number(maxRetries) || 0) + 1);

    // 整条事务排队：上一条请求拿到响应（或重发耗尽）之前不发下一条，
    // 否则设备只答最后一条，先发的那条会被"抢走"响应而白白重发。
    return this.enqueueRequest(async () => {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        // 先注册监听再发包，避免设备快速响应时错过 inputreport；
        // 超时计时在发包完成后才由 wait() 启动。
        const waiter = this.createResponseWaiter(expectedResponse);
        try {
          await this.sendOutputReport(reportId, data, sendOptions);
        } catch (error) {
          waiter.cancel();
          throw error;
        }

        const response = await waiter.wait(timeout);
        if (response) {
          if (attempt > 1) {
            console.log(`${label} 第 ${attempt} 次发送收到响应`);
            recordProtocol('event', `${label} 第 ${attempt} 次发送收到响应`);
          }
          return response;
        }

        if (attempt < maxAttempts) {
          console.warn(
            `${label} ${timeout}ms 内未响应，正在重发 (${attempt}/${maxRetries})`
          );
          recordProtocol(
            'event',
            `${label} ${timeout}ms 内未响应，正在重发 (${attempt}/${maxRetries})`
          );
        }
      }

      recordProtocol('error', `${label} 失败：设备连续 ${maxAttempts} 次未响应`);
      throw new Error(`${label} 失败：设备连续 ${maxAttempts} 次未响应`);
    });
  }

  /**
   * 发送宏读取请求，并等待对应索引的响应。
   * @param {Uint8Array} data - 已构建的命令7请求
   * @param {number} packetIndex - 期望响应的包索引
   * @param {number} type - 期望响应的宏数据类型
   * @param {{timeout?:number,maxRetries?:number}} options
   * @returns {Promise<Uint8Array>}
   */
  async sendMacroDataRequestWithRetry(data, packetIndex, type = 1, options = {}) {
    return this.sendRequestWithRetry(
      data,
      (response) =>
        parseCommandByte(response[0]).command === 0x07 &&
        response[1] === packetIndex &&
        response[3] === type,
      { label: `获取宏数据包 ${packetIndex}`, ...options }
    );
  }

  async changeProfile(profileIndex = 0) {
    console.log(`切换 Profile 索引 (索引 ${profileIndex})...`);
    const data = new Uint8Array(64);
    data[0] = 2;
    data[1] = 0x00;
    data[2] = 0x82 ;
    data[3] = 2;
    data[4] = profileIndex;
    return await this.sendOutputReport(0, data);
  }

  /**
   * 设置鼠标按键功能（命令3）
   * @param {number} profileIndex - Profile索引
   * @param {Object} buttonConfig - 按键配置对象
   * @returns {Promise<void>}
   */
  async setMouseButtonData(profileIndex, buttonConfig) {
    console.log(`设置鼠标按键信息 (Profile ${profileIndex})...`);
    const data = new Uint8Array(64);
    data[0] = 0x03;  // 命令3
    data[1] = 0x00;
    data[2] = 0x00 | 1;  // 写入标志
    data[3] = profileIndex;
    
    // 填充按键配置数据（每个按键4字节，Uint32LE）
    if (buttonConfig) {
      // TODO: 根据实际协议填充按键配置
      // 示例：data[4-7] = leftButton, data[8-11] = rightButton, etc.
    }
    
    return await this.sendOutputReport(0, data);
  }

  /**
   * 将设备回读的性能包规范为可回写的 64 字节缓冲
   * @param {Uint8Array} performanceData
   * @returns {Uint8Array}
   */
  clonePerformancePacket(performanceData) {
    if (!performanceData || performanceData.length < 14) {
      throw new Error('需要提供完整的性能数据');
    }
    return this.normalizeReportPayload(performanceData, this.defaultReportSize);
  }

  /**
   * 保留 / 推算 length 字段（data[2]）
   * @param {Uint8Array} data
   * @param {Uint8Array} source
   * @returns {number}
   */
  resolvePerformanceLength(data, source) {
    let length = 0;
    for (let i = data.length - 1; i >= 4; i--) {
      if (data[i] !== 0) {
        length = i - 3;
        break;
      }
    }
    if (source && source[2] > 0) {
      length = source[2];
    }
    return length || (data[2] || 0);
  }

  /**
   * 设置DPI档位（命令4）
   * 需要先获取性能数据类型1，修改level后以类型2发送
   * @param {number} dpiLevel - DPI档位（0-4）
   * @param {Uint8Array} performanceData - 当前的性能数据类型1的原始数据
   * @returns {Promise<void>}
   */
  async setDPILevel(dpiLevel, performanceData) {
    const data = this.clonePerformancePacket(performanceData);

    console.log(`设置DPI档位为 ${dpiLevel}...`);
    console.log('原始性能数据:', this.toHexString(performanceData.slice(0, 20)));

    const length = this.resolvePerformanceLength(data, performanceData);
    data[2] = length;
    // data[3] 是类型字段，改为类型2（写入）
    data[3] = 0x02;
    const layout = this.getDpiLayout(data, length);
    if (layout.currentLevelOffset == null) {
      throw new Error('当前协议未定义 type1 当前 DPI 档位写入字段');
    }
    data[layout.currentLevelOffset] = dpiLevel;

    console.log('修改后的数据:', this.toHexString(data.slice(0, 20)));
    console.log(`数据长度: ${length}, 类型: ${data[3]}`);

    await this.sendOutputReport(0, data);
    await this.sendConfirmation();
    return { success: true };
  }

  /**
   * 推算 type1 包中的 DPI 档位数
   * @param {Uint8Array} data
   * @param {number} lengthField
   * @returns {number}
   */
  resolveDpiLevelCount(data, lengthField) {
    if (this.isLegacyProtocol()) return 6;

    const payloadLength = (lengthField & 0x7f) || lengthField || 0;
    const settingsBytes = 11; // byte4-14
    const bytesPerLevel = 7; // 4 DPI + 3 RGB
    let count = Math.floor((payloadLength - settingsBytes) / bytesPerLevel);
    if (!Number.isFinite(count) || count <= 0) count = 7;
    return Math.max(1, Math.min(7, count));
  }

  /**
   * V3.0 及以下使用固定旧布局；未知版本也按旧布局保护性处理。
   */
  isLegacyProtocol() {
    if (!this.protocolVersion) return true;
    const match = String(this.protocolVersion).match(
      /(\d+)(?:\.(\d+))?(?:\.(\d+))?/
    );
    if (!match) return true;
    const major = Number(match[1]);
    const minor = Number(match[2] || 0);
    const patch = Number(match[3] || 0);
    return (
      major < 3 ||
      (major === 3 && minor === 0 && patch === 0)
    );
  }

  isProtocolAtLeast(targetMajor, targetMinor = 0, targetPatch = 0) {
    const match = String(this.protocolVersion || '').match(
      /(\d+)(?:\.(\d+))?(?:\.(\d+))?/
    );
    if (!match) return false;
    const current = [
      Number(match[1]),
      Number(match[2] || 0),
      Number(match[3] || 0),
    ];
    const target = [targetMajor, targetMinor, targetPatch];
    for (let i = 0; i < target.length; i++) {
      if (current[i] !== target[i]) return current[i] > target[i];
    }
    return true;
  }

  getDpiLayout(data, lengthField) {
    if (this.isLegacyProtocol()) {
      return {
        count: 6,
        tableOffset: 15,
        colorsOffset: 39,
        currentLevelOffset: 13,
      };
    }

    const count = this.resolveDpiLevelCount(data, lengthField);
    const tableOffset = 15;
    return {
      count,
      tableOffset,
      colorsOffset: tableOffset + count * 4,
      currentLevelOffset: 13,
    };
  }

  /**
   * 写入完整 type1 性能包（命令4 / 类型2）并确认
   * @param {Uint8Array} packet64
   * @returns {Promise<{success:boolean, raw:Uint8Array}>}
   */
  async writePerformanceType1(packet64) {
    const data = this.clonePerformancePacket(packet64);
    data[0] = data[0] || 0x04;
    data[3] = 0x02;
    const length = this.resolvePerformanceLength(data, packet64);
    data[2] = length || data[2];
    await this.sendOutputReport(0, data);
    await this.sendConfirmation();
    return { success: true, raw: data };
  }

  /**
   * 一次性写入多档 DPI（以及可选当前档位），只发 1 次 type2 + 确认
   * @param {(number|{x:number,y?:number})[]} dpiValues - 各档 DPI；
   *        传数字表示 X/Y 相同，传 {x, y} 可分别设置两轴
   * @param {Uint8Array} performanceData - type1 raw
   * @param {{ currentLevel?: number }} [options]
   * @returns {Promise<{success:boolean, raw:Uint8Array}>}
   */
  async setDPITable(dpiValues, performanceData, options = {}) {
    const data = this.clonePerformancePacket(performanceData);
    const length = this.resolvePerformanceLength(data, performanceData);
    data[2] = length;
    data[3] = 0x02;

    const layout = this.getDpiLayout(data, length);
    const count = layout.count;
    const list = Array.isArray(dpiValues) ? dpiValues : [];
    const n = Math.min(count, list.length, 7);

    const clampDpi = (v, fallback) =>
      Math.max(100, Math.min(30000, Number(v) > 0 ? Number(v) : fallback));

    for (let i = 0; i < n; i++) {
      const entry = list[i];
      const isPair = entry && typeof entry === 'object';
      const dpiX = clampDpi(isPair ? entry.x ?? entry.dpi : entry, 800);
      const dpiY = clampDpi(isPair ? entry.y ?? entry.dpiY ?? dpiX : entry, dpiX);
      const offset = layout.tableOffset + i * 4;
      data[offset] = dpiX & 0xff;
      data[offset + 1] = (dpiX >> 8) & 0xff;
      data[offset + 2] = dpiY & 0xff;
      data[offset + 3] = (dpiY >> 8) & 0xff;
    }

    if (
      layout.currentLevelOffset != null &&
      options.currentLevel != null &&
      Number.isFinite(options.currentLevel)
    ) {
      data[layout.currentLevelOffset] = Math.max(
        0,
        Math.min(count - 1, options.currentLevel | 0)
      );
    }

    console.log(`批量设置 DPI 表 (${n} 档)...`, list.slice(0, n));
    console.log('修改后的数据:', this.toHexString(data.slice(0, 24)));
    return this.writePerformanceType1(data);
  }

  /**
   * 设置DPI值（命令4）
   * 需要先获取性能数据类型1，修改对应档位的DPI值后以类型2发送
   * @param {number} dpiValue - DPI值（100-30000）
   * @param {number} dpiLevel - DPI档位（0-6）
   * @param {Uint8Array} performanceData - 当前的性能数据类型1的原始数据
   * @returns {Promise<void>}
   */
  async setDPI(dpiValue, dpiLevel, performanceData) {
    const data = this.clonePerformancePacket(performanceData);

    console.log(`设置档位 ${dpiLevel} 的DPI值为 ${dpiValue}...`);
    console.log('原始性能数据:', this.toHexString(performanceData.slice(0, 20)));

    const length = this.resolvePerformanceLength(data, performanceData);
    data[2] = length;
    data[3] = 0x02;

    const layout = this.getDpiLayout(data, length);
    if (dpiLevel >= 0 && dpiLevel < layout.count) {
      const offset = layout.tableOffset + dpiLevel * 4;
      data[offset] = dpiValue & 0xff;
      data[offset + 1] = (dpiValue >> 8) & 0xff;
      data[offset + 2] = dpiValue & 0xff;
      data[offset + 3] = (dpiValue >> 8) & 0xff;
      console.log(`修改档位 ${dpiLevel} (字节${offset}-${offset + 3}) 的DPI值为: ${dpiValue}`);
    }

    console.log('修改后的数据:', this.toHexString(data.slice(0, 20)));
    console.log(`数据长度: ${length}, 类型: ${data[3]}`);

    await this.sendOutputReport(0, data);
    await this.sendConfirmation();
    return { success: true };
  }

  /**
   * 设置 DPI 档位灯颜色（命令4，类型2写入）
   * 需要先获取性能数据类型1，修改对应档位的 RGB 后以类型2发送
   * @param {number} dpiLevel - DPI档位（0-6）
   * @param {number[]} rgb - RGB [R, G, B] (0-255)
   * @param {Uint8Array} performanceData - 当前的性能数据类型1的原始数据
   * @returns {Promise<void>}
   */
  async setDPIColor(dpiLevel, rgb, performanceData) {
    if (!Array.isArray(rgb) || rgb.length !== 3) {
      throw new Error('RGB 参数格式错误，应为 [R, G, B]');
    }

    const clampByte = (value) => Math.max(0, Math.min(255, Number(value) | 0));
    const r = clampByte(rgb[0]);
    const g = clampByte(rgb[1]);
    const b = clampByte(rgb[2]);
    const data = this.clonePerformancePacket(performanceData);

    console.log(`设置档位 ${dpiLevel} 的DPI颜色为 RGB(${r}, ${g}, ${b})...`);
    console.log('原始性能数据:', this.toHexString(performanceData.slice(0, 20)));

    const length = this.resolvePerformanceLength(data, performanceData);
    data[2] = length;
    data[3] = 0x02;

    const layout = this.getDpiLayout(data, length);
    const dpiLevelCount = layout.count;
    if (dpiLevel < 0 || dpiLevel >= dpiLevelCount) {
      throw new Error(`DPI档位越界: ${dpiLevel}，有效范围 0-${dpiLevelCount - 1}`);
    }

    const offset = layout.colorsOffset + dpiLevel * 3;
    if (offset + 2 >= data.length) {
      throw new Error(`颜色写入位置越界: offset=${offset}, dataLen=${data.length}`);
    }

    data[offset] = r;
    data[offset + 1] = g;
    data[offset + 2] = b;

    console.log('修改后的数据:', this.toHexString(data.slice(0, 20)));
    console.log(`数据长度: ${length}, 类型: ${data[3]}, DPI档位数量: ${dpiLevelCount}`);

    await this.sendOutputReport(0, data);
    await this.sendConfirmation();
    return { success: true };
  }

  /**
   * 设置有线和/或无线回报率（命令4，类型4写入）。
   * V4.0 以下只有完整包 byte8 一个字段，同时控制有线和无线。
   * V4.0：bit7=1 时写 payload 52-55（完整包 56-59）的自定义 Hz。
   * @param {{
   *   wiredRate?:number,
   *   wirelessRate?:number,
   *   wiredCustom?:boolean,
   *   wirelessCustom?:boolean
   * }} rates
   * @param {Uint8Array} performanceData - 当前的性能数据类型3的原始数据
   * @returns {Promise<{success:boolean, raw:Uint8Array}>}
   */
  async setPollingRates(rates, performanceData) {
    const wiredRate = rates?.wiredRate;
    const wirelessRate = rates?.wirelessRate;
    const wiredCustom = !!rates?.wiredCustom;
    const wirelessCustom = !!rates?.wirelessCustom;
    const wiredLevel =
      wiredRate == null || wiredCustom
        ? undefined
        : pollingRateHzToLevel(wiredRate);
    const wirelessLevel =
      wirelessRate == null || wirelessCustom
        ? undefined
        : pollingRateHzToLevel(wirelessRate);

    if (wiredRate != null && !wiredCustom && wiredLevel == null) {
      throw new Error(`不支持的有线回报率: ${wiredRate}Hz`);
    }
    if (wirelessRate != null && !wirelessCustom && wirelessLevel == null) {
      throw new Error(`不支持的无线回报率: ${wirelessRate}Hz`);
    }
    if (
      wiredRate == null &&
      wirelessRate == null
    ) {
      throw new Error('至少需要提供一个回报率');
    }

    const isV4 = this.isProtocolAtLeast(4, 0);
    if (!isV4 && (wiredCustom || wirelessCustom)) {
      throw new Error('V4.0 以下协议不支持自定义回报率');
    }
    if (
      !isV4 &&
      wiredLevel !== undefined &&
      wirelessLevel !== undefined &&
      wiredLevel !== wirelessLevel
    ) {
      throw new Error('V4.0 以下协议的有线和无线回报率必须相同');
    }

    const wiredOffset = V4_TYPE3_OFFSETS.coredReportRate;
    const wirelessOffset = isV4
      ? V4_TYPE3_OFFSETS.wirelessReportRate
      : V4_TYPE3_OFFSETS.coredReportRate;
    const data = this.clonePerformancePacket(performanceData);

    if (wiredRate != null) {
      console.log(
        wiredCustom
          ? `设置有线自定义回报率为 ${wiredRate}Hz (byte${V4_TYPE3_OFFSETS.coredRpt})...`
          : `设置有线回报率为 ${wiredRate}Hz (等级 ${wiredLevel}, byte${wiredOffset})...`
      );
    }
    if (wirelessRate != null) {
      console.log(
        wirelessCustom
          ? `设置无线自定义回报率为 ${wirelessRate}Hz (byte${V4_TYPE3_OFFSETS.wireLessRpt})...`
          : `设置无线回报率为 ${wirelessRate}Hz (等级 ${wirelessLevel}, byte${wirelessOffset})...`
      );
    }
    console.log(
      `原始性能数据类型3（HEX/Byte索引）:\n${formatPacketWithByteIndexes(
        performanceData.slice(0, 37)
      )}`
    );

    let length = this.resolvePerformanceLength(data, performanceData);
    if (isV4 && (wiredCustom || wirelessCustom)) {
      length = Math.max(length, V4_TYPE3_CUSTOM_RATE_MIN_LENGTH - 4);
    }
    data[2] = length;
    data[3] = 0x04;

    if (wiredRate != null) {
      if (wiredCustom) {
        data[wiredOffset] = encodeReportRateByte(
          { custom: true },
          data[wiredOffset]
        );
        writeUint16LE(
          data,
          V4_TYPE3_OFFSETS.coredRpt,
          clampCustomReportRateHz(wiredRate)
        );
      } else {
        data[wiredOffset] = encodeReportRateByte(
          { custom: false, level: wiredLevel },
          data[wiredOffset]
        );
      }
    }
    if (wirelessRate != null) {
      if (wirelessCustom) {
        data[wirelessOffset] = encodeReportRateByte(
          { custom: true },
          data[wirelessOffset]
        );
        writeUint16LE(
          data,
          V4_TYPE3_OFFSETS.wireLessRpt,
          clampCustomReportRateHz(wirelessRate)
        );
      } else {
        data[wirelessOffset] = encodeReportRateByte(
          { custom: false, level: wirelessLevel },
          data[wirelessOffset]
        );
      }
    }

    console.log(
      `修改后的系统参数（HEX/Byte索引）:\n${formatPacketWithByteIndexes(
        data.slice(0, 61)
      )}`
    );
    console.log(
      `length字段: 0x${data[2].toString(16).padStart(2, '0').toUpperCase()}, 类型: ${data[3]}`
    );

    await this.sendOutputReport(0, data);
    await this.sendConfirmation();
    return { success: true, raw: data };
  }

  /**
   * 将休眠时间写入 command=4/type=3 性能包，并转换为 type=4 写包。
   *
   * V4.0 与 V4.0 以下协议的两个字段都位于完整包 byte4-byte7，
   * 但其余 type3 字段及 length 不同，因此必须克隆对应协议的原始包。
   *
   * 单位为秒（uint16，0-65535）。传 sleepTime 表示有线/2.4G 与蓝牙统一，
   * 同一个值自动写入 byte4-5 与 byte6-7 两组字节；
   * 需要分别设置时仍可传 SysSleepTime / bleSysSleepTime，且优先级更高。
   *
   * @param {{sleepTime?:number,SysSleepTime?:number,bleSysSleepTime?:number}} sleepTimes
   * @param {Uint8Array} performanceData - 当前协议的性能数据类型3原始数据
   * @returns {Uint8Array}
   */
  encodeSleepTimes(sleepTimes, performanceData) {
    const data = this.clonePerformancePacket(performanceData);
    const length = this.resolvePerformanceLength(data, performanceData);
    const isV4 = this.isProtocolAtLeast(4, 0);
    const minimumPacketLength = isV4 ? 52 : 20;

    if (data.length < minimumPacketLength) {
      throw new Error(
        `${isV4 ? 'V4.0' : 'V4.0 以下'} 性能数据长度不足: ${data.length}`
      );
    }

    const normalizeTime = (value, fieldName) => {
      if (value == null) return undefined;
      const number = Number(value);
      if (!Number.isInteger(number) || number < 0 || number > 0xffff) {
        throw new Error(`${fieldName} 必须是 0-65535 之间的整数`);
      }
      return number;
    };

    // 统一值：未单独指定时，有线/2.4G 与蓝牙两组字节写同一个秒数
    const unified = normalizeTime(sleepTimes?.sleepTime, 'sleepTime');
    const sysSleepTime =
      normalizeTime(sleepTimes?.SysSleepTime, 'SysSleepTime') ?? unified;
    const bleSysSleepTime =
      normalizeTime(sleepTimes?.bleSysSleepTime, 'bleSysSleepTime') ?? unified;
    if (sysSleepTime === undefined && bleSysSleepTime === undefined) {
      throw new Error('至少需要提供一个休眠时间');
    }

    data[2] = length;
    data[3] = 0x04;
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    if (sysSleepTime !== undefined) {
      view.setUint16(4, sysSleepTime, true);
    }
    if (bleSysSleepTime !== undefined) {
      view.setUint16(6, bleSysSleepTime, true);
    }
    return data;
  }

  /**
   * 设置 2.4G/有线与 BLE 模式下的鼠标休眠时间（秒）。
   *
   * @param {{sleepTime?:number,SysSleepTime?:number,bleSysSleepTime?:number}} sleepTimes
   * @param {Uint8Array} performanceData - 当前协议的性能数据类型3原始数据
   * @returns {Promise<{success:boolean,raw:Uint8Array}>}
   */
  async setSleepTimes(sleepTimes, performanceData) {
    const data = this.encodeSleepTimes(sleepTimes, performanceData);

    console.log(
      `设置鼠标休眠时间: SysSleepTime=${
        sleepTimes?.SysSleepTime ?? sleepTimes?.sleepTime ?? '保持不变'
      }s, bleSysSleepTime=${
        sleepTimes?.bleSysSleepTime ?? sleepTimes?.sleepTime ?? '保持不变'
      }s (${this.isProtocolAtLeast(4, 0) ? 'V4.0' : 'V4.0 以下'} 协议)`
    );
    await this.sendOutputReport(0, data);
    await this.sendConfirmation();
    return { success: true, raw: data };
  }

  /**
   * 把按键性能（原 HITS）字段写进 command=4/type=3 性能包，并转成 type=4 写包。
   *
   * 只有 4.0 协议有 byte27 起的按键性能字段。byte27（按键类型）由固件决定，
   * 这里只读不写；其余未涉及的字段沿用设备回读值。
   *
   * @param {Object} settings - 界面模型 { left, right, other, fastButtonEnable }
   * @param {Uint8Array} performanceData - 性能数据类型3原始数据
   * @returns {{packet: Uint8Array, fields: Object}}
   */
  encodeButtonPerformance(settings, performanceData) {
    if (!this.isProtocolAtLeast(4, 0)) {
      throw new Error('按键性能设置仅 4.0 协议支持');
    }
    if (!settings) {
      throw new Error('缺少按键性能参数');
    }

    const data = this.clonePerformancePacket(performanceData);
    if (data.length < V4_TYPE3_MIN_LENGTH) {
      throw new Error(`4.0 性能数据长度不足: ${data.length}`);
    }

    const length = this.resolvePerformanceLength(data, performanceData);
    data[2] = length;
    data[3] = 0x04;
    const fields = applyButtonPerformanceToPacket(data, settings);
    return { packet: data, fields };
  }

  /**
   * 4.0 RT 实时监测：byte3 固定 0xF1，byte4=1 开始推送 / byte4=0 结束。
   * 设备会持续回最新采样，这里只发包，不等待单次响应。
   */
  encodeRtMonitorControl(action) {
    if (!this.isProtocolAtLeast(4, 0)) {
      throw new Error('RT 数据监测仅 4.0 协议支持');
    }
    return encodeRtMonitorControl(action);
  }

  async startRtMonitor() {
    const data = this.encodeRtMonitorControl(RT_MONITOR_START);
    console.log('开始获取 RT/ADC 数据 (byte4=1)');
    await this.sendOutputReport(0, data);
  }

  async stopRtMonitor() {
    const data = this.encodeRtMonitorControl(RT_MONITOR_STOP);
    console.log('结束获取 RT/ADC 数据 (byte4=0)');
    await this.sendOutputReport(0, data);
  }

  /**
   * 4.0 RT 档位监测：byte3 固定 0xF2，byte4=1 开始推送 / byte4=0 结束。
   * 推送数据与 0xF1 布局一致，给档位界面的按下动画用。
   */
  encodeRtGearControl(action) {
    if (!this.isProtocolAtLeast(4, 0)) {
      throw new Error('RT 档位监测仅 4.0 协议支持');
    }
    return encodeRtGearControl(action);
  }

  async startRtGearMonitor() {
    const data = this.encodeRtGearControl(RT_MONITOR_START);
    console.log('开始获取 RT 档位数据 (byte3=0xF2, byte4=1)');
    await this.sendOutputReport(0, data);
  }

  async stopRtGearMonitor() {
    const data = this.encodeRtGearControl(RT_MONITOR_STOP);
    console.log('结束获取 RT 档位数据 (byte3=0xF2, byte4=0)');
    await this.sendOutputReport(0, data);
  }

  /**
   * 4.0 RT 校准：byte3 固定 0xF0，byte4 是唯一数据字节。
   * byte4=1 放开阶段 / byte4=2 按下阶段 / byte4=0xFF 保存并结束。
   * 当前只发包做联调，不等待单次响应。
   */
  encodeRtCalibrateControl(value) {
    if (!this.isProtocolAtLeast(4, 0)) {
      throw new Error('RT 校准仅 4.0 协议支持');
    }
    return encodeRtCalibrateControl(value);
  }

  async setRtCalibratePhase(phase) {
    const data = this.encodeRtCalibrateControl(phase);
    console.log(`RT 校准阶段 (byte3=0xF0, byte4=${phase})`);
    await this.sendOutputReport(0, data);
  }

  async saveRtCalibrate() {
    const data = this.encodeRtCalibrateControl(RT_CALIBRATE_SAVE);
    console.log('保存 RT 校准 (byte3=0xF0, byte4=0xFF)');
    await this.sendOutputReport(0, data);
  }

  /**
   * 4.0 RT 校准配置：byte3 固定 0xF3。
   * 开始包 byte4=1，带左右各 10 字节配置；结束包 byte4=0；
   * 保存包 byte4=2，byte5-8 是用户/出厂魔数。
   * 设备先回 byte4=1，再回 byte4=0xFF，byte5 左 / byte6 右（0/1/2）。
   */
  encodeRtCalibrateConfigStart(left, right) {
    if (!this.isProtocolAtLeast(4, 0)) {
      throw new Error('RT 校准配置仅 4.0 协议支持');
    }
    return buildRtCalibrateConfigStartPacket(left, right);
  }

  async startRtCalibrateConfig(left, right) {
    const data = this.encodeRtCalibrateConfigStart(left, right);
    console.log('开始 RT 校准配置 (byte3=0xF3, byte4=1)');
    const canWait = Boolean(this.device?.addEventListener);
    const ackWaiter = canWait
      ? this.createResponseWaiter(isRtCalibrateConfigAck)
      : null;
    const resultWaiter = canWait
      ? this.createResponseWaiter(isRtCalibrateConfigResult)
      : null;
    this.rtCalibrateConfigWaiters = [ackWaiter, resultWaiter].filter(Boolean);
    try {
      await this.sendOutputReport(0, data);
      if (!canWait) return { success: null };
      const ack = await ackWaiter.wait(RT_CALIBRATE_CONFIG_ACK_TIMEOUT_MS);
      if (!ack) throw new Error('设备未确认开始校准');
      const result = await resultWaiter.wait(
        RT_CALIBRATE_CONFIG_RESULT_TIMEOUT_MS
      );
      const parsed = parseRtCalibrateConfigResult(result);
      if (!parsed) throw new Error('等待校准结果超时');
      return parsed;
    } finally {
      this.rtCalibrateConfigWaiters = null;
      ackWaiter?.cancel();
      resultWaiter?.cancel();
    }
  }

  async stopRtCalibrateConfig() {
    if (!this.isProtocolAtLeast(4, 0)) {
      throw new Error('RT 校准配置仅 4.0 协议支持');
    }
    this.rtCalibrateConfigWaiters?.forEach((waiter) => waiter.cancel());
    this.rtCalibrateConfigWaiters = null;
    const data = encodeRtCalibrateConfigControl(0);
    console.log('结束 RT 校准配置 (byte3=0xF3, byte4=0)');
    await this.sendOutputReport(0, data);
  }

  async saveRtCalibrateConfig(token) {
    if (!this.isProtocolAtLeast(4, 0)) {
      throw new Error('RT 校准配置仅 4.0 协议支持');
    }
    const data = encodeRtCalibrateConfigSave(token);
    console.log(
      `保存 RT 校准配置 (byte3=0xF3, byte4=2, token=0x${(Number(token) >>> 0)
        .toString(16)
        .padStart(8, '0')})`
    );
    await this.sendOutputReport(0, data);
  }

  /**
   * 下发按键性能设置（命令4 / 类型4）并确认。
   *
   * @param {Object} settings - 界面模型
   * @param {Uint8Array} performanceData - 性能数据类型3原始数据
   * @returns {Promise<{success:boolean, raw:Uint8Array, fields:Object}>}
   */
  async setButtonPerformance(settings, performanceData) {
    const { packet, fields } = this.encodeButtonPerformance(
      settings,
      performanceData
    );

    console.log(
      `设置按键性能: 左键模式=${fields.leftButtonMode}, 右键模式=${
        fields.rightButtonMode
      }, 快速响应位=0x${fields.fastButtonEnable
        .toString(16)
        .padStart(2, '0')
        .toUpperCase()}${
        canLinkButtons(fields.leftButtonMode, fields.rightButtonMode)
          ? '（左右键同类型）'
          : '（左右键类型不同，分别配置）'
      }`
    );
    console.log(
      `按键性能写包（HEX/Byte索引）:\n${formatPacketWithByteIndexes(
        packet.slice(0, V4_TYPE3_MIN_LENGTH)
      )}`
    );

    await this.sendOutputReport(0, packet);
    await this.sendConfirmation();
    return { success: true, raw: packet, fields };
  }

  /**
   * 把滚轮设置（滚轮延时使能与无效/有效时长）写进 command=4/type=3 性能包，
   * 并转成 type=4 写包。只动 byte49-53，其余字段沿用设备回读值。
   *
   * @param {{delayEnabled:boolean,startMs:number,endMs:number}} settings
   * @param {Uint8Array} performanceData - 性能数据类型3原始数据
   * @returns {{packet: Uint8Array, fields: Object}}
   */
  encodeWheelSettings(settings, performanceData) {
    if (!this.isProtocolAtLeast(4, 0)) {
      throw new Error('滚轮延时设置仅 4.0 协议支持');
    }
    if (!settings) {
      throw new Error('缺少滚轮设置参数');
    }

    const data = this.clonePerformancePacket(performanceData);
    assertType3Packet(data);

    const length = this.resolvePerformanceLength(data, performanceData);
    data[2] = length;
    data[3] = 0x04;
    const fields = applyWheelSettingsToPacket(data, settings);
    return { packet: data, fields };
  }

  /**
   * 下发滚轮设置（命令4 / 类型4）并确认。
   *
   * @param {{delayEnabled:boolean,startMs:number,endMs:number}} settings
   * @param {Uint8Array} performanceData - 性能数据类型3原始数据
   * @returns {Promise<{success:boolean, raw:Uint8Array, fields:Object}>}
   */
  async setWheelSettings(settings, performanceData) {
    const { packet, fields } = this.encodeWheelSettings(
      settings,
      performanceData
    );

    console.log(
      `设置滚轮延时: ${fields.wheelDelayEnable ? '开启' : '关闭'}, ` +
        `无效期=${fields.wheelDelayTimeStart}ms, ` +
        `有效期=${fields.wheelDelayTimeEnd}ms（自无效期结束起算）`
    );
    console.log(
      `滚轮写包（HEX/Byte索引）:\n${formatPacketWithByteIndexes(
        packet.slice(0, V4_TYPE3_MIN_LENGTH)
      )}`
    );

    await this.sendOutputReport(0, packet);
    await this.sendConfirmation();
    return { success: true, raw: packet, fields };
  }

  /**
   * 读取灯光设置（命令5，type=1 读取全部）。
   * payload 最长 87 字节，设备分包回应，续包由上层按索引逐个请求。
   */
  async getLightingData() {
    return this.getLightingDataByIndex(0);
  }

  /**
   * 请求指定索引的灯光数据包（命令5）。
   *
   * 和宏一样，byte2 必须带 bit7（本次请求到此为止），否则设备会一直等后续
   * 请求包而不回应。
   *
   * @param {number} packetIndex - 包索引
   */
  async getLightingDataByIndex(packetIndex = 0) {
    console.log(`获取灯光数据包 (索引 ${packetIndex})...`);
    const data = new Uint8Array(64);
    data[0] = LIGHTING_COMMAND;
    data[1] = packetIndex;
    data[2] = 0x80 | 1;
    data[3] = LIGHTING_READ_TYPE;
    return this.sendRequestWithRetry(
      data,
      (response) =>
        parseCommandByte(response[0]).command === LIGHTING_COMMAND &&
        response[1] === packetIndex &&
        response[3] === LIGHTING_READ_TYPE,
      { label: `获取灯光数据包 ${packetIndex}` }
    );
  }

  /**
   * 写入灯光设置（命令5，type=2 写入全部）。
   *
   * payload 超过单包容量，按宏的方式拆包发送；整个多包写入 + 确认包必须作为
   * 一条事务排队，中间插进别的请求会让逐包应答互相错位。
   *
   * @param {{effectSleepTime:number,effect:number,color:number,speed:number,brightness:number,direction:number,fade:number,customerRGB:number[]}} state
   * @param {Uint8Array} [basePayload] - 读回的完整 payload（不含 4 字节包头）
   * @returns {Promise<{success:boolean, raw:Uint8Array}>}
   */
  async setLightingSettings(state, basePayload = null) {
    return this.enqueueRequest(() =>
      this.writeLightingPackets(state, basePayload)
    );
  }

  /** @see setLightingSettings —— 已在事务队列内执行，不要直接调用 */
  async writeLightingPackets(state, basePayload = null) {
    const payload = encodeLightingPayload(state, basePayload);
    const packets = splitLightingPackets(payload, LIGHTING_WRITE_TYPE);
    console.log(
      `设置灯光: effect=${state.effect}, color=${state.color}, speed=${state.speed}, ` +
        `brightness=${state.brightness}, direction=${state.direction}, ` +
        `fade=${state.fade}, effectSleepTime=${state.effectSleepTime}s；` +
        `payload ${payload.length} 字节，拆成 ${packets.length} 个包`
    );

    for (let i = 0; i < packets.length; i++) {
      await this.sendLightingPacketWithRetry(
        packets[i],
        i,
        LIGHTING_WRITE_TYPE,
        packets.length
      );
    }

    await this.sendConfirmation();
    return { success: true, raw: payload };
  }

  /**
   * 发一个灯光数据包并等设备回同 index/type 的应答，超时按 requestMaxRetries
   * 重发；中途失败只记日志，全部重试用尽才抛错。
   *
   * @param {Uint8Array} packet - 已构建的 64 字节灯光包
   * @param {number} index - 包索引（byte1），应答必须同索引
   * @param {number} type - byte3，应答必须同类型
   * @param {number} total - 本次一共几个包，仅用于日志
   * @returns {Promise<Uint8Array>} 匹配到的应答
   */
  async sendLightingPacketWithRetry(packet, index, type, total = 1) {
    const maxAttempts = Math.max(1, this.requestMaxRetries + 1);
    let response = null;
    let lastSendError = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const responseWaiter = this.createResponseWaiter(
        (raw) =>
          parseCommandByte(raw[0]).command === LIGHTING_COMMAND &&
          raw[1] === index &&
          raw[3] === type
      );
      try {
        await this.sendOutputReport(0, packet);
      } catch (error) {
        responseWaiter.cancel();
        lastSendError = error;
        if (attempt < maxAttempts) {
          console.warn(
            `灯光数据包 ${index + 1} 发送失败，正在重发 ` +
              `(${attempt}/${this.requestMaxRetries}):`,
            error
          );
          continue;
        }
        break;
      }
      console.log(`灯光数据包 ${index + 1}/${total} 第 ${attempt} 次发送完成`);
      response = await responseWaiter.wait(this.requestTimeout);
      if (response) break;

      if (attempt < maxAttempts) {
        console.warn(
          `灯光数据包 ${index + 1} ${this.requestTimeout}ms 内未收到有效应答，` +
            `正在重发 (${attempt}/${this.requestMaxRetries})`
        );
      }
    }

    if (!response) {
      const reason = lastSendError
        ? `；最后一次发送错误：${lastSendError.message || lastSendError}`
        : '';
      throw new Error(
        `灯光数据包 ${index + 1} 失败：设备连续 ${maxAttempts} 次未返回 ` +
          `command=${LIGHTING_COMMAND}/index=${index}/type=${type}` +
          reason
      );
    }

    return response;
  }

  /**
   * 单独设置一个灯效（命令5，type=4）。
   *
   * payload = effect 序号(1) + effect_info_t(28)，单包装得下。灯光界面只改当前
   * 灯效自己的参数时走这里，不必整包回写；切换灯光模式与改 effectSleepTime
   * 仍要 {@link setLightingSettings}。
   *
   * @param {{effect:number,color:number,speed:number,brightness:number,direction:number,fade:number,customerRGB:number[]}} state
   * @param {Uint8Array} [basePayload] - 读回的完整 payload（不含 4 字节包头）
   * @returns {Promise<{success:boolean, raw:Uint8Array}>}
   */
  async setLightingEffect(state, basePayload = null) {
    return this.enqueueRequest(() =>
      this.writeLightingEffectPacket(state, basePayload)
    );
  }

  /** @see setLightingEffect —— 已在事务队列内执行，不要直接调用 */
  async writeLightingEffectPacket(state, basePayload = null) {
    const payload = encodeEffectWritePayload(state, basePayload);
    const [packet] = splitLightingPackets(payload, LIGHTING_EFFECT_WRITE_TYPE);
    console.log(
      `单独设置灯效: effect=${state.effect}, color=${state.color}, ` +
        `speed=${state.speed}, brightness=${state.brightness}, ` +
        `direction=${state.direction}, fade=${state.fade}；` +
        `payload ${payload.length} 字节`
    );

    await this.sendLightingPacketWithRetry(packet, 0, LIGHTING_EFFECT_WRITE_TYPE);
    await this.sendConfirmation();
    return { success: true, raw: payload };
  }

  /**
   * 切换灯效（命令5，type=5）。
   *
   * payload = effect 序号(1) + 保留(1)，长度字段按 2 下发（byte2 = 0x80|2，
   * 按 1 下发设备不认）：设备拿 byte4 替换已存数据里的 effect，
   * 其余字节全部保持不变。切灯光模式只该做这件事，不能再走整包回写
   * （{@link setLightingSettings}）——那会把界面上的当前值一起写进设备的
   * effect_info_t 里。
   *
   * @param {number|{effect:number}} effect - 灯效序号或含 effect 的界面状态
   * @returns {Promise<{success:boolean, raw:Uint8Array}>}
   */
  async setLightingEffectMode(effect) {
    return this.enqueueRequest(() => this.writeLightingEffectSwitchPacket(effect));
  }

  /** @see setLightingEffectMode —— 已在事务队列内执行，不要直接调用 */
  async writeLightingEffectSwitchPacket(effect) {
    const payload = encodeEffectSwitchPayload(effect);
    const [packet] = splitLightingPackets(payload, LIGHTING_EFFECT_SWITCH_TYPE);
    console.log(
      `切换灯效: effect=${payload[0]}（byte3=5，长度 ${payload.length}，只改 effect）`
    );

    await this.sendLightingPacketWithRetry(
      packet,
      0,
      LIGHTING_EFFECT_SWITCH_TYPE
    );
    await this.sendConfirmation();
    return { success: true, raw: payload };
  }

  /**
   * 兼容原调用：单个回报率参数代表无线回报率。
   */
  async setPollingRate(pollingRate, performanceData) {
    return this.setPollingRates(
      { wirelessRate: pollingRate },
      performanceData
    );
  }

  /**
   * 设置单个鼠标按键映射（命令3）
   * @param {number} buttonIndex - 按键序号 (0=左键, 1=右键, 2=中键, 3=B4, 4=B5, 5=DPI, 6=reportRate, 7=Pair, 8=NC, 9=NC, 10=滚轮上, 11=滚轮下)
   * @param {string} keyValue - 键值字符串 (如 '0x0100F000')
   * @returns {Promise<void>}
   */
  async setButtonMapping(buttonIndex, keyValue) {
    console.log(`设置按键映射: 序号=${buttonIndex}, 键值=${keyValue}`);

    const data = new Uint8Array(64);
    data[0] = 0x03;  // 命令3 - 鼠标按键
    data[1] = 0x00;
    data[2] = 0x85;  // 长度
    data[3] = 4;     // 类型4
    data[4] = buttonIndex;  // 按键序号

    // 解析键值字符串 (如 '0x0100F000') 为4字节
    // 格式: 01 00 F0 00 (大端序)
    const hexValue = keyValue.replace('0x', '');
    data[5] = parseInt(hexValue.substring(0, 2), 16);  // 01
    data[6] = parseInt(hexValue.substring(2, 4), 16);  // 00
    data[7] = parseInt(hexValue.substring(4, 6), 16);  // F0
    data[8] = parseInt(hexValue.substring(6, 8), 16);  // 00

    console.log('发送数据:', this.toHexString(data.slice(0, 10)));

    await this.sendOutputReport(0, data);

    // 发送确认包
    await this.sendConfirmation();

    return { success: true };
  }

  /**
   * 辅助方法：等待指定时间
   * @param {number} ms - 毫秒数
   * @returns {Promise<void>}
   */
  async wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 发送宏数据到设备（支持多包拆分发送）
   *
   * 整个多包写入 + 确认包必须作为一条事务排队：中间插进别的请求会让写包的
   * 逐包应答和那条请求的响应互相错位。
   *
   * @param {Uint8Array} macroData - 完整的宏数据（包含总长度头部）
   * @returns {Promise<boolean>} 发送是否成功
   */
  async setMacroData(macroData) {
    return this.enqueueRequest(() => this.writeMacroDataPackets(macroData));
  }

  /** @see setMacroData —— 已在事务队列内执行，不要直接调用 */
  async writeMacroDataPackets(macroData) {
    console.log(`发送宏数据, 总长度: ${macroData.length} 字节`);

    const PAYLOAD_SIZE = 59;  // 每包数据大小 (byte4-62)
    const packetCount = Math.ceil(macroData.length / PAYLOAD_SIZE);

    console.log(`需要拆分成 ${packetCount} 个包发送`);

    for (let i = 0; i < packetCount; i++) {
      const packet = new Uint8Array(64);

      // byte0: 命令
      packet[0] = 0x07;

      // byte1: 包索引
      packet[1] = i;

      // 计算当前包的数据范围
      const startOffset = i * PAYLOAD_SIZE;
      const endOffset = Math.min(startOffset + PAYLOAD_SIZE, macroData.length);
      const currentPayloadLength = endOffset - startOffset;

      // byte2: 当前包的数据长度
      packet[2] = i === packetCount - 1 ? 0x80 | currentPayloadLength : currentPayloadLength;

      // byte3: 类型（固定为2，表示写入宏数据）
      packet[3] = 0x02;

      // byte4-62: 实际数据
      for (let j = 0; j < currentPayloadLength; j++) {
        packet[4 + j] = macroData[startOffset + j];
      }

      // 发送并等待响应
      await this.sendOutputReport(0, packet);

      console.log(`包 ${i + 1}/${packetCount} 发送完成`);

      // 等待设备响应（最后一个包不需要等待）
      if (i < packetCount - 1) {
        await this.waitForResponse(0x07, 100);
      }
    }

    // 所有包发送完成后，发送确认包
    await this.sendConfirmation();

    console.log('宏数据发送完成');
    return true;
  }

  /**
   * 恢复出厂设置（命令9）
   * @returns {Promise<void>}
   */
  async factoryReset() {
    console.log('发送恢复出厂设置命令...');
    const data = new Uint8Array(64);
    data[0] = 9;          // byte0: 9
    data[1] = 0;          // byte1: 0
    data[2] = 0x80 | 2;   // byte2: 0x82
    data[3] = 0xFF;       // byte3: 0xFF
    data[4] = 0;          // byte4: 0
    return await this.sendOutputReport(0, data);
  }

  /**
   * 发送确认包到设备
   * 在完成DPI设置、按键映射、回报率、高级功能、配置文件常用模版等操作后调用
   * 必须在所有拆包数据都发送完成后才发送此确认包
   * @returns {Promise<void>}
   */
  async sendConfirmation() {
    console.log('发送确认包...');
    const data = new Uint8Array(64);
    data[0] = 10;         // byte0: 10
    data[1] = 0;          // byte1: 0
    data[2] = 0x80 | 1;   // byte2: 0x81
    data[3] = 1;          // byte3: 1
    return await this.sendOutputReport(0, data);
  }

  /**
   * 把期望响应描述统一转成匹配函数。
   * @param {number|null|{command?:number,deviceType?:number,type?:number}|((data:Uint8Array,event:InputReportEvent)=>boolean)} expectedCommand
   * @returns {(data:Uint8Array,event?:InputReportEvent)=>boolean}
   */
  static buildResponseMatcher(expectedCommand) {
    if (typeof expectedCommand === 'function') {
      return (data, event) => Boolean(expectedCommand(data, event));
    }
    if (expectedCommand === null || expectedCommand === undefined) {
      return () => true;
    }
    if (typeof expectedCommand === 'object') {
      return (data) => matchesProtocolResponse(data, expectedCommand);
    }
    return (data) => parseCommandByte(data[0]).command === expectedCommand;
  }

  /**
   * 创建响应监听器：立即注册 inputreport 监听，但超时计时到 wait() 才开始。
   *
   * 重发逻辑必须「先监听、后发包」，否则设备极快响应时会漏掉 inputreport；
   * 而写操作要排写队列，若计时随监听一起启动，排队耗时会被算进
   * 100ms 响应窗口里，造成误判超时。分离两步即可两者兼顾。
   *
   * @param {number|null|object|Function} expectedCommand - 期望响应描述
   * @returns {{wait:(timeout?:number)=>Promise<Uint8Array|null>, cancel:()=>void}}
   */
  createResponseWaiter(expectedCommand) {
    const responseDevice = this.device;
    if (!responseDevice?.addEventListener) {
      return { wait: async () => null, cancel: () => {} };
    }

    const match = HIDSender.buildResponseMatcher(expectedCommand);
    let settled = false;
    let received = null;
    let resolveWait = null;
    let timer = null;

    const cleanup = () => {
      if (timer != null) {
        clearTimeout(timer);
        timer = null;
      }
      responseDevice.removeEventListener('inputreport', handler);
    };

    function handler(event) {
      if (settled) return;
      const view = event.data;
      const offset = view.byteOffset || 0;
      const data = new Uint8Array(
        view.buffer.slice(offset, offset + view.byteLength)
      );
      if (!match(data, event)) return;
      settled = true;
      received = data;
      cleanup();
      if (resolveWait) resolveWait(data);
    }

    responseDevice.addEventListener('inputreport', handler);

    return {
      wait: (timeout = this.requestTimeout) =>
        new Promise((resolve) => {
          // 发包期间已经收到响应时直接返回，不再启动计时。
          if (settled) {
            resolve(received);
            return;
          }
          resolveWait = resolve;
          timer = setTimeout(() => {
            settled = true;
            cleanup();
            resolve(null);  // 超时不报错，交由调用方决定是否重发
          }, timeout);
        }),
      cancel: () => {
        if (settled) return;
        settled = true;
        cleanup();
        if (resolveWait) resolveWait(null);
      },
    };
  }

  /**
   * 等待设备响应
   * @param {number|null|{command?:number,deviceType?:number,type?:number}|((data:Uint8Array,event:InputReportEvent)=>boolean)} expectedCommand
   *   数字表示完整匹配 byte0；null 表示接收下一条报告；函数用于按协议
   *   自定义匹配，例如只比较 command 位而忽略 byte0 的 bit7。
   * @param {number} timeout - 超时时间(ms)
   * @returns {Promise<Uint8Array|null>} 响应数据，超时返回null
   */
  waitForResponse(expectedCommand, timeout = 100) {
    return this.createResponseWaiter(expectedCommand).wait(timeout);
  }

  /**
   * 清理资源
   */
  destroy() {
    this.device = null;
    this.protocolVersion = null;
    this.connectionType = 'wired';
    this.deviceAccessAllowed = false;
  }
}

export default HIDSender;
